import { logger } from 'utils/logger';
import { StreamMedia } from 'utils/webrtc/receiving/StreamMedia';
import { extractMediaStreamState, MediaStreamDiagnostics } from './streamStateExtractor';

export type AudioMonitorState = {
  feedId: string;
  stream: StreamMedia;
  element: HTMLAudioElement;
};

export type AudioStateResolver = () => AudioMonitorState | null;

export type AudioRecovery = (mid: string) => void;

type AudioListenerName = 'playing' | 'pause' | 'stalled' | 'error';

const AUDIO_HEALTH_CHECK_MS = 5000;
const HAVE_CURRENT_DATA = 2; // HTMLMediaElement.readyState: playout has started
// Muted duration before we give up on "not yet arrived" and report it — well past normal RTP arrival.
const MUTED_DELIVERY_DEADLINE_MS = 15000;

const hasPlayoutStarted = (diagnostics: MediaStreamDiagnostics): boolean =>
  !!diagnostics.mediaElementCurrentTime ||
  (diagnostics.mediaElementReadyState ?? 0) >= HAVE_CURRENT_DATA;

// Live, unmuted track (RTP flowing) but playout never started. The live+unmuted gate
// excludes legitimately-silent feeds, so this only fires on a provably-stuck element.
const isStuckButLive = (diagnostics: MediaStreamDiagnostics, playingObserved: boolean): boolean => {
  if (playingObserved) {
    return false;
  }
  if (diagnostics.trackReadyState !== 'live' || diagnostics.trackMuted === true) {
    return false;
  }
  return !hasPlayoutStarted(diagnostics);
};

export class AudioElementMonitor {
  private mid: string | null = null;

  private element: HTMLAudioElement | null = null;

  private resolveState: AudioStateResolver | null = null;

  private recovery: AudioRecovery | null = null;

  private playingObserved = false;

  private listeners: Record<string, EventListener> = {};

  private healthTimer: ReturnType<typeof setTimeout> | null = null;

  private checkCount = 0;

  private trackAwaitingUnmute: MediaStreamTrack | null = null;

  private trackUnmuteHandler: (() => void) | null = null;

  private deliveryDeadlineTimer: ReturnType<typeof setTimeout> | null = null;

  private neverDeliveredReported = false;

  attach(
    mid: string,
    element: HTMLAudioElement,
    resolveState: AudioStateResolver,
    recovery?: AudioRecovery
  ): void {
    this.dispose();

    this.mid = mid;
    this.element = element;
    this.resolveState = resolveState;
    this.recovery = recovery ?? null;

    this.logHealth('Attached');

    this.addListener('playing', this.onPlaying, { once: true });
    this.addListener('pause', this.onPause);
    this.addListener('stalled', this.onStalled);
    this.addListener('error', this.onError);

    this.armHealthTimer();
  }

  dispose(): void {
    if (this.healthTimer !== null) {
      clearTimeout(this.healthTimer);
      this.healthTimer = null;
    }
    this.clearTrackUnmuteWait();
    if (this.element) {
      for (const [name, fn] of Object.entries(this.listeners)) {
        this.element.removeEventListener(name, fn);
      }
    }
    this.listeners = {};
    this.element = null;
    this.resolveState = null;
    this.recovery = null;
    this.playingObserved = false;
    this.checkCount = 0;
    this.neverDeliveredReported = false;
    this.mid = null;
  }

  // Used when play() timed out with RTP already flowing — don't wait for the armed timer.
  checkNow(): void {
    this.checkPlaybackHealth();
  }

  private logHealth(event: string): void {
    const state = this.resolveState?.();
    if (!state || !this.mid) {
      return;
    }
    const payload = extractMediaStreamState(state.feedId, this.mid, state.stream, state.element);
    logger.remote({ system: true, capture: 'streaming' }).debug(`[AUDIO-HEALTH] ${event}`, payload);
  }

  private checkPlaybackHealth = (): void => {
    const state = this.resolveState?.();
    if (!state || !this.mid) {
      return;
    }

    this.checkCount += 1;
    const diagnostics = extractMediaStreamState(
      state.feedId,
      this.mid,
      state.stream,
      state.element
    );
    logger
      .remote({ system: true, capture: 'streaming' })
      .debug('[AUDIO-HEALTH] health check', { ...diagnostics, checkCount: this.checkCount });

    if (isStuckButLive(diagnostics, this.playingObserved)) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn('[AUDIO-HEALTH] audio attached but not playing — triggering recovery', {
          mid: this.mid,
          feedId: state.feedId,
          elementId: state.element.id,
          checkCount: this.checkCount,
        });
      this.recovery?.(this.mid);
      return;
    }

    if (this.playingObserved || hasPlayoutStarted(diagnostics)) {
      return;
    }
    if (diagnostics.trackReadyState === 'ended') {
      return;
    }
    this.scheduleRecheck(state);
  };

  // A muted or not-yet-arrived track makes the stuck verdict inconclusive: DTX and Janus-level
  // mute pause RTP, and remote tracks start muted until the first packet. Re-judge once RTP flows.
  private scheduleRecheck(state: AudioMonitorState): void {
    const track = this.resolveTrack(state);
    if (track?.muted) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('[AUDIO-HEALTH] health check deferred — track muted, awaiting RTP', {
          mid: this.mid,
          feedId: state.feedId,
          checkCount: this.checkCount,
        });
      this.awaitTrackUnmute(track);
      return;
    }
    this.armHealthTimer();
  }

  private resolveTrack(state: AudioMonitorState): MediaStreamTrack | null {
    try {
      return state.stream.getTrack?.() ?? null;
    } catch {
      return null;
    }
  }

  private awaitTrackUnmute(track: MediaStreamTrack): void {
    this.clearTrackUnmuteWait();
    const handler = () => {
      this.clearTrackUnmuteWait();
      this.armHealthTimer();
    };
    track.addEventListener('unmute', handler, { once: true });
    this.trackAwaitingUnmute = track;
    this.trackUnmuteHandler = handler;
    // A track that never unmutes would defer forever with no signal — bound the wait and
    // report once past the deadline; the unmute listener stays armed so a late start still recovers.
    this.deliveryDeadlineTimer = setTimeout(() => {
      this.deliveryDeadlineTimer = null;
      this.reportNeverDelivered();
    }, MUTED_DELIVERY_DEADLINE_MS);
  }

  private clearTrackUnmuteWait(): void {
    if (this.deliveryDeadlineTimer !== null) {
      clearTimeout(this.deliveryDeadlineTimer);
      this.deliveryDeadlineTimer = null;
    }
    if (this.trackAwaitingUnmute && this.trackUnmuteHandler) {
      this.trackAwaitingUnmute.removeEventListener('unmute', this.trackUnmuteHandler);
    }
    this.trackAwaitingUnmute = null;
    this.trackUnmuteHandler = null;
  }

  // warn-level only — a track can stay legitimately muted (mic off, DTX), so this must not
  // escalate to a captured Sentry event.
  private reportNeverDelivered(): void {
    const state = this.resolveState?.();
    if (!state || !this.mid || this.neverDeliveredReported) {
      return;
    }
    this.neverDeliveredReported = true;
    const diagnostics = extractMediaStreamState(
      state.feedId,
      this.mid,
      state.stream,
      state.element
    );
    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('[AUDIO-HEALTH] audio never delivered — track muted past delivery deadline', {
        ...diagnostics,
        deadlineMs: MUTED_DELIVERY_DEADLINE_MS,
      });
  }

  private armHealthTimer(): void {
    if (this.healthTimer !== null) {
      clearTimeout(this.healthTimer);
    }
    this.healthTimer = setTimeout(() => {
      this.healthTimer = null;
      this.checkPlaybackHealth();
    }, AUDIO_HEALTH_CHECK_MS);
  }

  private onPlaying = (): void => {
    this.playingObserved = true;
    this.logHealth('Playing event');
  };

  private onPause = (): void => {
    const state = this.resolveState?.();
    if (!state || !this.mid) {
      return;
    }
    logger
      .remote({ system: true, capture: 'streaming' })
      .warn(`[AUDIO-HEALTH] Audio paused unexpectedly`, {
        mid: this.mid,
        feedId: state.feedId,
        elementId: state.element.id,
      });
  };

  private onStalled = (): void => {
    const state = this.resolveState?.();
    if (!state || !this.mid) {
      return;
    }
    logger.remote({ system: true, capture: 'streaming' }).warn(`[AUDIO-HEALTH] Audio stalled`, {
      mid: this.mid,
      feedId: state.feedId,
      elementId: state.element.id,
    });
  };

  private onError = (event: Event): void => {
    const state = this.resolveState?.();
    if (!state || !this.mid) {
      return;
    }
    const errorEvent = event as ErrorEvent;
    logger
      .remote({ system: true, capture: 'streaming' })
      .error(`[AUDIO-HEALTH] Audio element error`, {
        mid: this.mid,
        feedId: state.feedId,
        elementId: state.element.id,
        error: errorEvent.error,
        message: errorEvent.message,
      });
  };

  private addListener(
    name: AudioListenerName,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    if (!this.element) {
      return;
    }
    this.element.addEventListener(name, handler, options);
    this.listeners[name] = handler;
  }
}
